<template>
  <div>
    <div class="prospect-drawer-backdrop" @click="close"></div>
    <aside class="prospect-drawer" role="dialog" aria-label="Lead detail" @keydown.esc="close">
      <header class="drawer-header">
        <div class="flex-1" style="min-width: 0;">
          <div v-if="!editingFamilyContact" class="flex align-items-center gap-x-xs">
            <h3 class="m-y-none font-heavy text-20">{{ family.contact_name || 'Unnamed contact' }}</h3>
            <button
              type="button"
              class="btn btn-xs btn-default"
              title="Edit contact"
              @click="startEditFamilyContact"
            >
              <i class="fa-solid fa-pencil"></i>
            </button>
          </div>
          <div v-else class="flex gap-x-xs flex-wrap">
            <input
              v-model="familyContactDraft.contact_first_name"
              type="text"
              class="form-control input-sm"
              placeholder="First name"
              style="max-width: 140px;"
            />
            <input
              v-model="familyContactDraft.contact_last_name"
              type="text"
              class="form-control input-sm"
              placeholder="Last name"
              style="max-width: 140px;"
            />
          </div>
          <p
            v-if="!editingFamilyContact && (headerAttention || headerStage)"
            class="m-y-none drawer-subtitle"
            :class="headerAttention ? headerAttention.className : ''"
          >
            <template v-if="headerAttention">
              <i :class="headerAttention.icon"></i>
              {{ headerAttention.text }}
              <span v-if="headerStage" class="drawer-subtitle-divider"> · </span>
            </template>
            <span v-if="headerStage" class="drawer-subtitle-stage">{{ headerStage }}</span>
          </p>
        </div>
        <button type="button" class="btn btn-default" aria-label="Close" @click="close">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </header>

      <div class="prospect-drawer-body p-md">
        <!-- Contact: quiet inline links. Always render all four channels —
             missing ones render as muted hints so a coach can see at a
             glance what's available without wondering "where's the phone?" -->
        <div v-if="!editingFamilyContact" class="drawer-contact m-b-md">
          <a v-if="telHref" :href="telHref" class="drawer-contact-link">
            <i class="fa-solid fa-phone"></i> {{ family.contact_phone }}
          </a>
          <span v-else class="drawer-contact-link is-disabled" title="No phone on file">
            <i class="fa-solid fa-phone"></i> No phone
          </span>

          <a v-if="smsHref" :href="smsHref" class="drawer-contact-link" title="Text">
            <i class="fa-regular fa-comment"></i> Text
          </a>
          <span v-else class="drawer-contact-link is-disabled" title="No phone on file">
            <i class="fa-regular fa-comment"></i> Text
          </span>

          <a v-if="mailtoHref" :href="mailtoHref" class="drawer-contact-link">
            <i class="fa-solid fa-envelope"></i> {{ family.contact_email }}
          </a>
          <span v-else class="drawer-contact-link is-disabled" title="No email on file">
            <i class="fa-solid fa-envelope"></i> No email
          </span>
          <copy-icon
            v-if="family.contact_email"
            :text="family.contact_email"
            class="m-l-xxs"
          />

          <message-shortcut-button
            v-if="canDirectMessage"
            :profile-id="family.guardian.id"
            :profile-type="family.guardian.type"
            :profile-name="family.guardian.full_name"
            :current-profile-id="currentAuthorId"
            :current-profile-type="currentAuthorType"
            button-class="drawer-contact-link"
            button-label="WIQ message"
            button-title="Message in WIQ"
            icon-class="fa-solid fa-comments"
          />
          <span v-else class="drawer-contact-link is-disabled" :title="dmDisabledTitle">
            <i class="fa-solid fa-comments"></i> WIQ message
          </span>
        </div>

        <!-- Phone numbers found on linked profiles' registration answers.
             Trial-purchase leads skip the inquiry form, so the family record
             often has no phone even though the parent answered a phone
             question during signup. Suggest-and-confirm rather than silent
             backfill: a question can hold someone else's number, so the coach
             sees which section/question it came from. Emergency-contact
             answers are excluded server-side. -->
        <div v-if="!editingFamilyContact && phoneSuggestions.length" class="m-b-md">
          <div
            v-for="(suggestion, idx) in phoneSuggestions"
            :key="'phone-suggestion-' + idx"
            class="drawer-phone-suggestion"
            :class="{ 'm-t-xs': idx > 0 }"
          >
            <i class="fa-solid fa-wand-magic-sparkles"></i>
            <span class="font-semi">{{ suggestion.value }}</span>
            <span class="drawer-phone-suggestion-source">
              {{ suggestion.prompt }} · {{ suggestionSourceLabel(suggestion) }}
            </span>
            <button class="btn btn-xs btn-default" @click.stop="useSuggestedPhone(suggestion)">
              Use this number
            </button>
          </div>
        </div>

        <!-- Edit mode: phone + email inputs and save/cancel for the family contact. -->
        <div v-if="editingFamilyContact" class="m-b-md p-sm rounded-xs bg-gray-50">
          <div class="flex gap-x-xs flex-wrap m-b-xs">
            <input
              v-model="familyContactDraft.contact_phone"
              type="tel"
              class="form-control input-sm"
              placeholder="Phone"
              style="max-width: 200px;"
            />
            <input
              v-model="familyContactDraft.contact_email"
              type="email"
              class="form-control input-sm"
              placeholder="Email"
              style="max-width: 280px;"
            />
          </div>
          <div class="flex gap-x-xs">
            <button class="btn btn-xs btn-primary" @click.stop="saveFamilyContact">Save</button>
            <button class="btn btn-xs btn-default" @click.stop="cancelEditFamilyContact">Cancel</button>
          </div>
        </div>

        <!-- Section: Inquiry context -->
        <h4 class="drawer-section-heading">Inquiry</h4>
        <div class="row">
          <div class="col-sm-6">
            <dl class="m-b-none">
              <dt class="text-12 text-gray-500 m-b-xxs">Assigned Coach</dt>
              <dd class="m-b-sm">
                <select
                  class="form-control input-sm"
                  :value="family.assigned_coach ? family.assigned_coach.id : ''"
                  @change="$emit('assigned-coach-change', $event.target.value)"
                >
                  <option value="">Unassigned</option>
                  <option v-for="coach in coachOptions" :key="coach.id" :value="coach.id">
                    {{ coach.full_name }}
                  </option>
                </select>
              </dd>

              <dt class="text-12 text-gray-500 m-b-xxs">How They Heard About Us</dt>
              <dd class="m-b-sm">{{ family.hear_about_us || '\u2014' }}</dd>
            </dl>
          </div>
          <div class="col-sm-6">
            <dl class="m-b-none">
              <dt class="text-12 text-gray-500 m-b-xxs">Submitted via</dt>
              <dd class="m-b-sm">{{ sourceLabel(family.source) }}</dd>

              <!-- Ad attribution off the inquiry link's utm_* tags; only
                   shown when the lead actually arrived through one -->
              <template v-if="campaignLabel(family)">
                <dt class="text-12 text-gray-500 m-b-xxs">Campaign</dt>
                <dd class="m-b-sm">
                  {{ campaignLabel(family) }}
                  <span v-if="family.utm_term || family.utm_content" class="text-12 text-gray-500 block">
                    {{ [family.utm_term, family.utm_content].filter(Boolean).join(' / ') }}
                  </span>
                </dd>
              </template>

              <dt class="text-12 text-gray-500 m-b-xxs">Lead created</dt>
              <dd class="m-b-sm">{{ formatCreatedDate(family.created_at) }}</dd>
            </dl>
          </div>
        </div>

        <!-- Custom Question Answers (still part of Inquiry context) -->
        <div v-if="family.registration_answers && family.registration_answers.length > 0" class="m-t-sm">
          <label class="text-12 text-gray-500 font-semi m-b-xs">Custom responses</label>
          <div class="p-sm bg-gray-50 rounded-xs">
            <dl class="m-b-none">
              <template v-for="ra in family.registration_answers">
                <dt :key="'dt-' + ra.id" class="text-12 text-gray-500 m-b-xxs">{{ ra.question_prompt }}</dt>
                <dd :key="'dd-' + ra.id" class="m-b-sm">{{ ra.plain_text_answer || '\u2014' }}</dd>
              </template>
            </dl>
          </div>
        </div>

        <!-- Section: the linked guardian. Given its own heading + card rather
             than a row inside the Inquiry grid: the answers panel hangs off it,
             and a two-column dl had nowhere sensible to put that. Mirrors the
             wrestler card so both entities on the lead read the same way. -->
        <template v-if="family.guardian">
          <h4 class="drawer-section-heading">{{ guardianSectionLabel }}</h4>
          <div class="drawer-entity-card">
            <div class="flex align-items-center gap-sm flex-wrap">
              <a
                :href="guardianHref"
                class="drawer-entity-name-link font-semi text-16"
              >{{ family.guardian.full_name }}</a>
              <a
                :href="guardianHref"
                class="btn btn-xs btn-default m-l-auto"
                :title="'Open ' + guardianSectionLabel.toLowerCase() + ' profile'"
              >
                Profile <i class="fa-solid fa-right-long m-l-xxs"></i>
              </a>
            </div>

            <!-- Registration answers from the guardian's profile. Anchored at
                 the foot of the card, directly above the panel it opens. -->
            <button
              v-if="guardianAnswers.length"
              type="button"
              class="drawer-answers-toggle m-t-sm"
              :aria-expanded="isResponsesOpen('guardian') ? 'true' : 'false'"
              :title="'Registration answers from ' + family.guardian.full_name + '’s profile'"
              @click.stop="toggleResponses('guardian')"
            >
              <i
                class="drawer-answers-toggle-caret"
                :class="isResponsesOpen('guardian') ? 'fa-solid fa-caret-down' : 'fa-solid fa-caret-right'"
              ></i>
              Registration answers ({{ guardianAnswers.length }})
            </button>
            <div v-if="isResponsesOpen('guardian') && guardianAnswers.length" class="m-t-xs">
              <div class="p-sm bg-gray-50 rounded-xs">
                <dl class="m-b-none">
                  <template v-for="ra in guardianAnswers">
                    <dt :key="'gdt-' + ra.id" class="text-12 text-gray-500 m-b-xxs">{{ ra.question_prompt }}</dt>
                    <dd :key="'gdd-' + ra.id" class="m-b-sm">{{ ra.plain_text_answer || '—' }}</dd>
                  </template>
                </dl>
              </div>
            </div>
          </div>
        </template>

        <!-- Section: Wrestlers (the spine of the drawer) -->
        <h4 class="drawer-section-heading">Wrestlers</h4>
        <div
          v-for="(child, idx) in family.prospects"
          :key="'detail-' + child.id"
          class="drawer-entity-card"
          :class="{ 'm-t-sm': idx > 0 }"
        >
          <!-- Identity row. Kept separate from the action row below so the
               profile link can pin to the card's right edge: `m-l-auto` in a
               wrapping flex container aligns to whichever line the item lands
               on, so it only reads as "right edge" when the row is short. -->
          <div class="flex align-items-center gap-sm flex-wrap m-b-xs">
            <a
              v-if="child.wrestler_profile"
              :href="'/wrestlers/' + child.wrestler_profile.id"
              class="drawer-entity-name-link font-semi text-16"
            >{{ child.child_name || 'Unnamed wrestler' }}</a>
            <span v-else class="font-semi text-16">{{ child.child_name || 'Unnamed wrestler' }}</span>
            <span v-if="child.child_date_of_birth" class="text-12 text-gray-500">DOB {{ formatDob(child.child_date_of_birth) }}</span>
            <span v-else-if="child.child_age" class="text-12 text-gray-500">Age {{ child.child_age }}</span>
            <span v-if="child.child_academic_class" class="text-12 text-gray-500">{{ child.child_academic_class }}</span>
            <button
              v-if="!child.wrestler_profile && editingChildId !== child.id"
              type="button"
              class="btn btn-xs btn-default"
              title="Edit wrestler details"
              @click.stop="startEditChild(child)"
            >
              <i class="fa-solid fa-pencil"></i>
            </button>
            <a
              v-if="child.wrestler_profile"
              :href="'/wrestlers/' + child.wrestler_profile.id"
              class="btn btn-xs btn-default m-l-auto"
              title="Open wrestler profile"
            >
              Profile <i class="fa-solid fa-right-long m-l-xxs"></i>
            </a>
          </div>

          <!-- Action row: current stage and the controls that change it. -->
          <div class="flex align-items-center gap-sm flex-wrap m-b-sm">
            <span class="p-x-xs p-y-xxs rounded-xs text-12 font-semi" :class="stageBadgeClass(child.stage)">
              {{ childStageLabel(child) }}
            </span>
            <span style="position: relative;">
              <button class="btn btn-xs btn-default" @click.stop="toggleStageDropdown(child.id)">
                Change stage <span class="caret"></span>
              </button>
              <ul
                v-if="stageDropdownId === child.id"
                class="dropdown-menu"
                style="display: block;"
              >
                <li
                  v-for="stage in allStages"
                  :key="stage.value"
                  :class="{ 'active': child.stage === stage.value }"
                >
                  <a href="javascript:void(0)" @click.stop="selectStage(child, stage.value)">
                    {{ stage.label }}
                  </a>
                </li>
              </ul>
            </span>
            <button
              v-if="!isTerminalStage(child.stage)"
              type="button"
              class="prospect-followup-toggle"
              :class="child.needs_follow_up ? 'is-on' : 'is-off'"
              :title="child.needs_follow_up ? 'Click to clear follow-up' : 'Click to mark for follow-up'"
              @click.stop="$emit('follow-up-toggle', { child, needs_follow_up: !child.needs_follow_up })"
            >
              <i :class="child.needs_follow_up ? 'fa-solid fa-flag' : 'fa-regular fa-flag'"></i>
              <span v-if="child.needs_follow_up">
                Following up
                <span v-if="child.follow_up_set_at" class="prospect-followup-toggle-meta">
                  · {{ followUpSubtext(child) }}
                </span>
              </span>
              <span v-else>Mark for follow-up</span>
            </button>
          </div>

          <div class="row">
            <div class="col-sm-6">
              <dl class="m-b-none">
                <dt class="text-12 text-gray-500 m-b-xxs">Experience</dt>
                <dd class="m-b-xs">{{ child.experience_level || '\u2014' }}</dd>

                <dt class="text-12 text-gray-500 m-b-xxs">Trial scheduled</dt>
                <dd class="m-b-xs">
                  <div v-if="editingTrialDateId !== child.id" class="flex align-items-center gap-x-xs flex-wrap">
                    <span v-if="child.trial_event">{{ child.trial_event.name }} · {{ formatTrialDate(child.trial_event.start_at) }}</span>
                    <span v-else-if="child.trial_scheduled_at">{{ formatTrialDate(child.trial_scheduled_at) }}</span>
                    <span v-else class="text-gray-400">Not set</span>
                    <button class="btn btn-xs btn-default" title="Edit the date only" @click.stop="startEditTrialDate(child)">
                      <i class="fa-solid fa-pencil"></i>
                    </button>
                    <button
                      v-if="canBookTrialClass(child)"
                      class="btn btn-xs btn-default"
                      @click.stop="toggleTrialBooking(child)"
                    >
                      {{ trialBookingLabel(child) }}
                    </button>
                    <!-- The pass is spent, so there is nothing left to book into.
                         Said plainly here because the picker's own empty state
                         ("your trial may still be setting up") is written for a
                         parent mid-purchase and reads as a bug to a coach. -->
                    <span v-else-if="trialPassSpent(child)" class="text-12 text-gray-500">{{ trialPassSpentLabel(child) }}</span>
                  </div>
                  <div v-else class="flex gap-x-xs flex-wrap">
                    <input v-model="editTrialDateText" type="datetime-local" class="form-control input-sm" />
                    <button class="btn btn-xs btn-primary" @click.stop="saveTrialDate(child)">Save</button>
                    <button class="btn btn-xs btn-default" @click.stop="cancelEditTrialDate">Cancel</button>
                    <button
                      v-if="child.trial_scheduled_at"
                      class="btn btn-xs btn-link text-red-600"
                      @click.stop="clearTrialDate(child)"
                    >Clear</button>
                  </div>
                </dd>
              </dl>
            </div>
            <div class="col-sm-6">
              <dl class="m-b-none">
                <dt v-if="child.paid_session" class="text-12 text-gray-500 m-b-xxs">Trial session</dt>
                <dd v-if="child.paid_session" class="m-b-xs">{{ child.paid_session.name }}</dd>

                <dt v-if="child.trial_pass_summary" class="text-12 text-gray-500 m-b-xxs">Trial passes</dt>
                <dd v-if="child.trial_pass_summary" class="m-b-xs">{{ trialPassText(child.trial_pass_summary) }}</dd>

                <dt v-if="child.stage === 'converted'" class="text-12 text-gray-500 m-b-xxs">Converted via</dt>
                <dd v-if="child.stage === 'converted'" class="m-b-xs">{{ convertedViaText(child) }}</dd>

                <dt v-if="child.lost_reason" class="text-12 text-gray-500 m-b-xxs">Lost reason</dt>
                <dd v-if="child.lost_reason" class="m-b-xs">{{ child.lost_reason }}</dd>

                <dt v-if="child.last_contacted_at" class="text-12 text-gray-500 m-b-xxs">Last contacted</dt>
                <dd v-if="child.last_contacted_at" class="m-b-xs">{{ timeAgo(child.last_contacted_at) }}</dd>
              </dl>
            </div>
          </div>

          <!-- Class booking / rescheduling. Sits below the two-column detail
               row rather than inside the "Trial scheduled" cell: the picker
               brings its own date strip and class cards, which have nowhere to
               go in a half column at the drawer's 560px. Keyed on the booked
               class so confirming a reschedule remounts it against fresh
               eligible events instead of leaving the old selection on screen. -->
          <div
            v-if="bookingChildId === child.id"
            class="m-t-sm p-sm bg-gray-50 rounded-xs"
          >
            <trial-class-picker
              :key="'trial-picker-' + child.id + '-' + (child.trial_event_id || 'none')"
              :wrestler-id="child.wrestler_profile.id"
              :wrestler-name="child.child_name"
              :coach-view="true"
              @booked="onTrialClassBooked(child, $event)"
            ></trial-class-picker>
          </div>

          <!-- Registration answers from the linked wrestler profile. The
               disclosure control sits here rather than in the meta row above:
               that row wraps, so a toggle inside it shifted every chip after
               it on open/close and sat in a different spot on every card
               depending on which optional fields (DOB, class, link status)
               were present. Anchored here it never moves, and expanding
               only pushes content below it. -->
          <button
            v-if="wrestlerAnswers(child).length"
            type="button"
            class="drawer-answers-toggle m-t-sm"
            :aria-expanded="isResponsesOpen(wrestlerResponsesKey(child)) ? 'true' : 'false'"
            :title="'Registration answers from ' + child.child_name + '’s profile'"
            @click.stop="toggleResponses(wrestlerResponsesKey(child))"
          >
            <i
              class="drawer-answers-toggle-caret"
              :class="isResponsesOpen(wrestlerResponsesKey(child)) ? 'fa-solid fa-caret-down' : 'fa-solid fa-caret-right'"
            ></i>
            Registration answers ({{ wrestlerAnswers(child).length }})
          </button>
          <div v-if="isResponsesOpen(wrestlerResponsesKey(child)) && wrestlerAnswers(child).length" class="m-t-xs">
            <div class="p-sm bg-gray-50 rounded-xs">
              <dl class="m-b-none">
                <template v-for="ra in wrestlerAnswers(child)">
                  <dt :key="'wdt-' + ra.id" class="text-12 text-gray-500 m-b-xxs">{{ ra.question_prompt }}</dt>
                  <dd :key="'wdd-' + ra.id" class="m-b-sm">{{ ra.plain_text_answer || '—' }}</dd>
                </template>
              </dl>
            </div>
          </div>

          <!-- Child details edit panel (name / DOB / academic class) -->
          <div v-if="editingChildId === child.id" class="m-t-sm p-sm rounded-xs bg-gray-50">
            <label class="text-12 text-gray-500 m-b-xxs">Wrestler details</label>
            <div class="flex gap-x-xs flex-wrap m-b-xs">
              <input
                v-model="childDraft.child_first_name"
                type="text"
                class="form-control input-sm"
                placeholder="First name"
                style="max-width: 140px;"
              />
              <input
                v-model="childDraft.child_last_name"
                type="text"
                class="form-control input-sm"
                placeholder="Last name"
                style="max-width: 140px;"
              />
              <input
                v-model="childDraft.child_date_of_birth"
                type="date"
                class="form-control input-sm"
                style="max-width: 160px;"
              />
              <select v-model="childDraft.child_academic_class" class="form-control input-sm" style="max-width: 180px;">
                <option value="">Academic class…</option>
                <optgroup label="Pre-K to 12">
                  <option v-for="grade in k12Grades" :key="grade" :value="grade">{{ grade }}</option>
                </optgroup>
                <optgroup label="College">
                  <option v-for="grade in collegeGrades" :key="grade" :value="grade">{{ grade }}</option>
                </optgroup>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="flex gap-x-xs">
              <button class="btn btn-xs btn-primary" @click.stop="saveChild(child)">Save</button>
              <button class="btn btn-xs btn-default" @click.stop="cancelEditChild">Cancel</button>
            </div>
          </div>

          <!-- Lost reason prompt -->
          <div v-if="lostReasonChildId === child.id" class="m-t-sm p-sm rounded-xs bg-gray-50">
            <label class="text-12 text-gray-500">Why didn't {{ child.child_name }} join?</label>
            <div class="flex gap-x-xs flex-wrap">
              <input v-model="lostReasonText" type="text" class="form-control input-sm" placeholder="Enter reason..." />
              <button class="btn btn-xs btn-primary" @click.stop="confirmLostReason(child)">Save</button>
              <button class="btn btn-xs btn-default" @click.stop="cancelLostReason">Cancel</button>
            </div>
          </div>

          <!-- Trial date prompt on stage change -->
          <div v-if="trialDateChildId === child.id" class="m-t-sm p-sm rounded-xs bg-gray-50">
            <label class="text-12 text-gray-500">When is {{ child.child_name }}'s trial? (optional)</label>
            <div class="flex gap-x-xs flex-wrap">
              <input v-model="trialDateText" type="datetime-local" class="form-control input-sm" />
              <button class="btn btn-xs btn-primary" @click.stop="confirmTrialDate(child)">Save</button>
              <button class="btn btn-xs btn-default" @click.stop="cancelTrialDate">Cancel</button>
            </div>
          </div>
        </div>

        <!-- Section: Timeline -->
        <h4 class="drawer-section-heading">Timeline</h4>
        <p class="text-12 text-gray-500 m-b-sm">Stage changes, notes, and logged contacts — newest first. Stage entries show whether the system moved the lead or a coach did.</p>
        <notes-list
          :base-url="familyNotesUrl"
          :create-url="familyNotesUrl"
          :current-author-id="currentAuthorId"
          :current-author-type="currentAuthorType"
          :mention-suggest-url="mentionSuggestUrl"
          :access-key="accessKey"
          :auth-token="authToken"
          :cloudfront-base-url="cloudfrontBaseUrl"
          :endpoint="endpoint"
          :activity-types="activityTypes"
          :family="family"
          :timeline-events="stageChanges"
          :as-timeline="true"
          @note-created="$emit('note-created')"
          @stage-change="$emit('stage-change', $event)"
        ></notes-list>
      </div>
    </aside>
  </div>
</template>

<script>
import NotesList from './notes_list.vue';
import CopyIcon from './copy_icon.vue';
import MessageShortcutButton from './message_shortcut_button.vue';
import TrialClassPicker from './trial_class_picker.vue';
import {
  stageLabel, childStageLabel, followUpReasonLabel, suggestionPromptLabel,
} from './prospectStageLabels';

export default {
  name: 'prospect-drawer',
  components: {
    NotesList,
    CopyIcon,
    MessageShortcutButton,
    TrialClassPicker,
  },
  props: {
    family: { type: Object, required: true },
    familiesApiUrl: { type: String, required: true },
    teamName: { type: String, default: '' },
    // IANA zone id (team.default_time_zone) so "Lead created" renders in the
    // club's local time rather than the viewer's.
    teamTimezone: { type: String, default: '' },
    coachOptions: { type: Array, default: () => [] },
    currentAuthorId: { type: Number, required: true },
    currentAuthorType: { type: String, required: true },
    mentionSuggestUrl: { type: String, default: '' },
    accessKey: { type: String, default: '' },
    authToken: { type: String, default: '' },
    cloudfrontBaseUrl: { type: String, default: '' },
    endpoint: { type: String, default: '' },
  },
  data() {
    return {
      stageDropdownId: null,
      editingTrialDateId: null,
      // Prospect id whose class picker is expanded; only one at a time so two
      // open pickers can't race each other's booking POSTs.
      bookingChildId: null,
      editTrialDateText: '',
      lostReasonChildId: null,
      lostReasonText: '',
      trialDateChildId: null,
      trialDateText: '',
      editingFamilyContact: false,
      familyContactDraft: {
        contact_first_name: '',
        contact_last_name: '',
        contact_email: '',
        contact_phone: '',
      },
      editingChildId: null,
      childDraft: {
        child_first_name: '',
        child_last_name: '',
        child_date_of_birth: '',
        child_academic_class: '',
      },
      // Stage-change audit rows for every wrestler in the family, shown
      // interleaved with notes in the Timeline section.
      stageChanges: [],
      // Registration answers grouped by linked profile (guardian + wrestler
      // profiles), fetched on drawer open. Source for the phone suggestions
      // and the per-profile "Custom responses" panels.
      linkedProfiles: [],
      openResponseKeys: [],
    };
  },
  computed: {
    familyNotesUrl() {
      return `${this.familiesApiUrl}/${this.family.id}/notes`;
    },
    // Normalize parent-entered phone to E.164-ish for tel:/sms: links.
    phoneDigits() {
      const raw = this.family.contact_phone;
      if (!raw) return '';
      const cleaned = String(raw).replace(/[^\d+]/g, '');
      if (cleaned.startsWith('+')) return cleaned;
      if (cleaned.length === 10) return `+1${cleaned}`;
      if (cleaned.length === 11 && cleaned.startsWith('1')) return `+${cleaned}`;
      return cleaned;
    },
    telHref() {
      return this.phoneDigits ? `tel:${this.phoneDigits}` : '';
    },
    smsHref() {
      return this.phoneDigits ? `sms:${this.phoneDigits}` : '';
    },
    mailtoHref() {
      if (!this.family.contact_email) return '';
      if (!this.teamName) return `mailto:${this.family.contact_email}`;
      const subject = encodeURIComponent(`Wrestling at ${this.teamName}`);
      return `mailto:${this.family.contact_email}?subject=${subject}`;
    },
    guardianHref() {
      if (!this.family.guardian) return '';
      const base = this.family.guardian.type === 'CoachProfile' ? '/coaches/' : '/parents/';
      return base + this.family.guardian.id;
    },
    // A lead's guardian is usually a parent, but coaches enrolling their own
    // kids link to a CoachProfile — the section heading follows suit.
    guardianSectionLabel() {
      if (!this.family.guardian) return 'Parent';
      return this.family.guardian.type === 'CoachProfile' ? 'Coach' : 'Parent';
    },
    // Phone numbers pulled from linked profiles' RegPhoneNumberQuestion
    // answers, server-side batch lookup (family.phone_suggestions). Only
    // offered while the family record itself has no phone — once one is
    // saved the suggestions disappear.
    phoneSuggestions() {
      if (this.phoneDigits) return [];
      return (this.family.phone_suggestions || []).map((s) => ({
        value: s.value,
        prompt: suggestionPromptLabel(s),
        profileName: s.profile_name,
        profileType: s.profile_type,
        relation: s.relation,
      }));
    },
    guardianAnswers() {
      if (!this.family.guardian) return [];
      const group = this.linkedProfiles.find(
        (lp) => lp.relation === 'guardian'
          && lp.profile_type === this.family.guardian.type
          && lp.profile_id === this.family.guardian.id,
      );
      return group ? group.registration_answers : [];
    },
    canDirectMessage() {
      if (!this.family.guardian) return false;
      if (!this.currentAuthorId || !this.currentAuthorType) return false;
      return ['ParentProfile', 'CoachProfile'].includes(this.family.guardian.type);
    },
    dmDisabledTitle() {
      if (!this.family.guardian) return 'No linked parent or coach account';
      return 'Cannot send a WIQ message';
    },
    allStages() {
      return [
        { label: 'Interested', value: 'inquiry' },
        { label: 'Trial Scheduled', value: 'trial_scheduled' },
        { label: 'Trialing', value: 'trialing' },
        { label: 'Trial Complete', value: 'trial_complete' },
        { label: 'Converted', value: 'converted' },
        { label: "Didn't Join", value: 'didnt_join' },
        { label: 'Archived', value: 'archived' },
      ];
    },
    k12Grades() {
      return [
        'Pre-K', 'Kindergarten',
        '1st grade', '2nd grade', '3rd grade', '4th grade', '5th grade', '6th grade',
        '7th grade', '8th grade', '9th grade', '10th grade', '11th grade', '12th grade',
      ];
    },
    collegeGrades() {
      return [
        'Freshmen', 'RS Freshmen', 'Sophomore', 'RS Sophomore',
        'Junior', 'RS Junior', 'Senior', 'RS Senior', 'Grad Student',
      ];
    },
    activityTypes() {
      return [
        { value: 'phone_call', label: 'Phone Call' },
        { value: 'sms', label: 'Text' },
        { value: 'email', label: 'Email' },
        { value: 'in_person', label: 'In Person' },
        { value: 'other', label: 'Other' },
      ];
    },
    mostUrgentChild() {
      if (!this.family.prospects || this.family.prospects.length === 0) return null;
      const flagged = this.family.prospects.filter((c) => c.needs_follow_up);
      if (flagged.length > 0) {
        // Oldest follow-up flag wins.
        return flagged.reduce((oldest, c) => {
          const a = oldest.follow_up_set_at ? new Date(oldest.follow_up_set_at).getTime() : 0;
          const b = c.follow_up_set_at ? new Date(c.follow_up_set_at).getTime() : 0;
          return b && (!a || b < a) ? c : oldest;
        });
      }
      const trialChildren = this.family.prospects
        .filter((c) => c.trial_scheduled_at)
        .sort((a, b) => new Date(a.trial_scheduled_at) - new Date(b.trial_scheduled_at));
      if (trialChildren.length > 0) return trialChildren[0];
      return this.family.prospects[0];
    },
    headerAttention() {
      const child = this.mostUrgentChild;
      if (!child) return null;

      // Upcoming trial (within 48h)
      if (child.trial_scheduled_at) {
        const diffMs = new Date(child.trial_scheduled_at) - new Date();
        const diffHours = diffMs / 3600000;
        if (diffHours > 0 && diffHours <= 48) {
          return {
            text: this.trialPillText(child),
            icon: 'fa-solid fa-calendar-days',
            className: 'is-upcoming-trial',
          };
        }
      }

      // Needs attention (follow-up flag is set)
      if (this.family.needs_attention) {
        const reason = this.attentionReason(child);
        if (reason) {
          const prefix = this.family.prospects.length > 1 ? `${child.child_name}: ` : '';
          const days = this.daysSinceFollowUp(child);
          const overdue = days > 3;
          return {
            text: prefix + reason,
            icon: overdue ? 'fa-solid fa-circle-exclamation' : 'fa-regular fa-bell',
            className: overdue ? 'is-overdue' : 'is-today',
          };
        }
      }

      return null;
    },
    headerStage() {
      const child = this.mostUrgentChild || (this.family.prospects && this.family.prospects[0]);
      if (!child) return '';
      if (this.family.prospects.length > 1) {
        return `${this.family.prospects.length} wrestlers`;
      }
      return this.childStageLabel(child);
    },
  },
  watch: {
    // Drawer instance is reused when switching between families.
    'family.id': function onFamilyChange() {
      this.loadStageChanges();
      this.loadLinkedAnswers();
    },
    // Fires when a child's stage is patched (parent $sets the prospects
    // array entry) so the timeline picks up the fresh audit row.
    'family.prospects': function onProspectsChange() {
      this.loadStageChanges();
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onEsc);
    document.addEventListener('click', this.closeStageDropdown);
    this.loadStageChanges();
    this.loadLinkedAnswers();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onEsc);
    document.removeEventListener('click', this.closeStageDropdown);
  },
  methods: {
    close() {
      this.$emit('close');
    },
    loadStageChanges() {
      // A family rarely accumulates more than a handful of transitions, so a
      // single generous page covers it.
      axios.get(`${this.familiesApiUrl}/${this.family.id}/stage_changes?per_page=100`)
        .then((response) => {
          this.stageChanges = response.data.stage_changes || [];
        })
        .catch(() => {
          this.stageChanges = [];
        });
    },
    loadLinkedAnswers() {
      this.linkedProfiles = [];
      this.openResponseKeys = [];
      axios.get(`${this.familiesApiUrl}/${this.family.id}/linked_answers`)
        .then((response) => {
          this.linkedProfiles = response.data.linked_profiles || [];
        })
        .catch(() => {
          this.linkedProfiles = [];
        });
    },
    wrestlerAnswers(child) {
      if (!child.wrestler_profile) return [];
      const group = this.linkedProfiles.find(
        (lp) => lp.relation === 'wrestler' && lp.profile_id === child.wrestler_profile.id,
      );
      return group ? group.registration_answers : [];
    },
    wrestlerResponsesKey(child) {
      return `wrestler-${child.id}`;
    },
    toggleResponses(key) {
      const idx = this.openResponseKeys.indexOf(key);
      if (idx === -1) {
        this.openResponseKeys.push(key);
      } else {
        this.openResponseKeys.splice(idx, 1);
      }
    },
    isResponsesOpen(key) {
      return this.openResponseKeys.includes(key);
    },
    suggestionSourceLabel(suggestion) {
      let role = 'Parent';
      if (suggestion.relation === 'wrestler') {
        role = 'Wrestler';
      } else if (suggestion.profileType === 'CoachProfile') {
        role = 'Coach';
      }
      return `${role}: ${suggestion.profileName}`;
    },
    useSuggestedPhone(suggestion) {
      const attrs = {
        contact_first_name: this.family.contact_first_name || '',
        contact_last_name: this.family.contact_last_name || '',
        contact_email: this.family.contact_email || '',
        contact_phone: suggestion.value,
      };
      this.$emit('family-contact-save', { family: this.family, attrs });
    },
    // Date + time in the club's timezone (falls back to the viewer's), with
    // the zone abbreviation so it's unambiguous for remote admins.
    formatCreatedDate(isoString) {
      if (!isoString) return '—';
      const tz = this.teamTimezone || moment.tz.guess();
      const m = moment.tz(isoString, tz);
      if (!m.isValid()) return '—';
      return m.format('MMM D, YYYY h:mm a z');
    },
    onEsc(e) {
      if (e.key === 'Escape') this.close();
    },
    stageLabel(stage) {
      return stageLabel(stage);
    },
    // Derives "Needs Scheduling" for a purchased-but-undated trial.
    childStageLabel(child) {
      return childStageLabel(child);
    },
    // "campaign (source / medium)" — whichever utm pieces the ad link carried
    campaignLabel(family) {
      const origin = [family.utm_source, family.utm_medium].filter(Boolean).join(' / ');
      if (family.utm_campaign && origin) return `${family.utm_campaign} (${origin})`;
      return family.utm_campaign || origin || '';
    },
    sourceLabel(source) {
      if (!source) return '—';
      const labels = {
        backfill: 'Imported',
        website_form: 'Website inquiry',
        website_form_quick: 'Website inquiry (quick form)',
        trial_registration: 'Trial sign-up',
      };
      return labels[source] || source;
    },
    stageBadgeClass(stage) {
      const classes = {
        inquiry: 'bg-blue-100 text-blue-800',
        trial_scheduled: 'bg-purple-100 text-purple-800',
        trialing: 'bg-green-100 text-green-800',
        trial_complete: 'bg-yellow-100 text-yellow-800',
        converted: 'bg-green-500 color-white',
        didnt_join: 'bg-red-100 text-red-800',
        archived: 'bg-gray-100 text-gray-800',
      };
      return classes[stage] || 'bg-gray-100 text-gray-800';
    },
    isTerminalStage(stage) {
      return ['converted', 'didnt_join', 'archived'].includes(stage);
    },
    attentionReason(child) {
      if (!child.needs_follow_up) return '';
      const reason = this.reasonLabel(child.follow_up_reason);
      const days = this.daysSinceFollowUp(child);
      if (!child.follow_up_set_at) return reason || 'Needs follow-up';
      if (days === 0) return reason ? `${reason} today` : 'Flagged today';
      const suffix = `${days} ${days === 1 ? 'day' : 'days'} ago`;
      return reason ? `${reason} · ${suffix}` : `Flagged ${suffix}`;
    },
    reasonLabel(reason) {
      return followUpReasonLabel(reason);
    },
    formatDob(isoDate) {
      if (!isoDate) return '';
      // Parse as-is — the backend stores Y-m-d, no timezone baggage.
      const [y, m, d] = isoDate.split('-');
      if (!y || !m || !d) return isoDate;
      return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
    },
    convertedViaText(child) {
      const sessionName = child.conversion_paid_session && child.conversion_paid_session.name;
      if (sessionName) return sessionName;
      const subName = child.conversion_billing_subscription && child.conversion_billing_subscription.name;
      if (subName) return `${subName} (subscription)`;
      if (child.converted_via === 'subscription') return 'Subscription';
      if (child.converted_via === 'manual') return 'Coach (manual)';
      return '—';
    },
    // Compact "1 used · 2 expired of 3" summary for trial pass usage. Drops the
    // zero segments so a clean trial reads "1 used of 1" rather than padding
    // with "0 expired".
    trialPassText(summary) {
      if (!summary || !summary.total) return '';
      const parts = [];
      if (summary.used) parts.push(`${summary.used} used`);
      if (summary.expired) parts.push(`${summary.expired} expired`);
      if (summary.available) parts.push(`${summary.available} remaining`);
      if (parts.length === 0) return `${summary.total} total`;
      return `${parts.join(' · ')} of ${summary.total}`;
    },
    daysSinceFollowUp(child) {
      if (!child.follow_up_set_at) return 0;
      return Math.floor((new Date() - new Date(child.follow_up_set_at)) / 86400000);
    },
    followUpSubtext(child) {
      const reason = this.reasonLabel(child.follow_up_reason);
      const days = this.daysSinceFollowUp(child);
      const daysStr = days === 0 ? 'today' : `${days}d ago`;
      return reason ? `${reason} · ${daysStr}` : `flagged ${daysStr}`;
    },
    shortTimeAgo(dateString) {
      if (!dateString) return '';
      const diffMs = new Date() - new Date(dateString);
      const mins = Math.floor(diffMs / 60000);
      const hours = Math.floor(diffMs / 3600000);
      const days = Math.floor(diffMs / 86400000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days === 1) return '1d ago';
      return `${days}d ago`;
    },
    trialPillText(child) {
      if (!child || !child.trial_scheduled_at) return '';
      const diffMs = new Date(child.trial_scheduled_at) - new Date();
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      const prefix = this.family.prospects.length > 1 ? `${child.child_name}: ` : '';
      if (diffHours < 0) return `${prefix}trial in progress`;
      if (diffHours < 24) {
        const timeStr = new Date(child.trial_scheduled_at).toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: '2-digit',
        });
        return `${prefix}trial today ${timeStr}`;
      }
      if (diffDays === 1) return `${prefix}trial tomorrow`;
      return `${prefix}trial in ${diffDays} days`;
    },
    toggleStageDropdown(id) {
      this.stageDropdownId = this.stageDropdownId === id ? null : id;
    },
    closeStageDropdown() {
      this.stageDropdownId = null;
    },
    selectStage(child, newStage) {
      this.stageDropdownId = null;
      if (child.stage === newStage) return;

      if (newStage === 'didnt_join') {
        this.lostReasonChildId = child.id;
        this.lostReasonText = '';
        return;
      }

      if (newStage === 'trial_scheduled') {
        this.trialDateChildId = child.id;
        this.trialDateText = this.formatDatetimeLocal(child.trial_scheduled_at);
        return;
      }

      this.$emit('stage-change', { child, newStage, extra: {} });
    },
    confirmLostReason(child) {
      this.$emit('stage-change', {
        child,
        newStage: 'didnt_join',
        extra: { lost_reason: this.lostReasonText },
      });
      this.cancelLostReason();
    },
    cancelLostReason() {
      this.lostReasonChildId = null;
      this.lostReasonText = '';
    },
    confirmTrialDate(child) {
      const iso = this.trialDateText ? new Date(this.trialDateText).toISOString() : null;
      this.$emit('stage-change', {
        child,
        newStage: 'trial_scheduled',
        extra: { trial_scheduled_at: iso },
      });
      this.cancelTrialDate();
    },
    cancelTrialDate() {
      this.trialDateChildId = null;
      this.trialDateText = '';
    },
    startEditFamilyContact() {
      this.familyContactDraft = {
        contact_first_name: this.family.contact_first_name || '',
        contact_last_name: this.family.contact_last_name || '',
        contact_email: this.family.contact_email || '',
        contact_phone: this.family.contact_phone || '',
      };
      this.editingFamilyContact = true;
    },
    cancelEditFamilyContact() {
      this.editingFamilyContact = false;
    },
    saveFamilyContact() {
      const attrs = {
        contact_first_name: (this.familyContactDraft.contact_first_name || '').trim(),
        contact_last_name: (this.familyContactDraft.contact_last_name || '').trim(),
        contact_email: (this.familyContactDraft.contact_email || '').trim(),
        contact_phone: (this.familyContactDraft.contact_phone || '').trim(),
      };
      this.$emit('family-contact-save', { family: this.family, attrs });
      this.editingFamilyContact = false;
    },
    startEditChild(child) {
      this.childDraft = {
        child_first_name: child.child_first_name || '',
        child_last_name: child.child_last_name || '',
        child_date_of_birth: child.child_date_of_birth || '',
        child_academic_class: child.child_academic_class || '',
      };
      this.editingChildId = child.id;
    },
    cancelEditChild() {
      this.editingChildId = null;
    },
    saveChild(child) {
      const attrs = {
        child_first_name: (this.childDraft.child_first_name || '').trim(),
        child_last_name: (this.childDraft.child_last_name || '').trim(),
        child_date_of_birth: this.childDraft.child_date_of_birth || null,
        child_academic_class: this.childDraft.child_academic_class || '',
      };
      this.$emit('child-details-save', { child, attrs });
      this.editingChildId = null;
    },
    // A class can only be booked into a live trial pass. Booking doesn't spend
    // the pass (check-in does), so an unattended trial stays reschedulable
    // right up to the class.
    canBookTrialClass(child) {
      const passes = child.trial_pass_summary;
      return !!(child.wrestler_profile && passes && passes.available > 0);
    },

    // Expired counts as spent: a pass that timed out unused leaves available
    // at 0 with used at 0, and without this the row would show a bare pencil
    // and no reason why there's nothing to book.
    trialPassSpent(child) {
      const passes = child.trial_pass_summary;
      return !!(passes && passes.available === 0 && (passes.used > 0 || passes.expired > 0));
    },

    trialPassSpentLabel(child) {
      return child.trial_pass_summary.used > 0 ? 'Trial used' : 'Trial pass expired';
    },

    trialBookingLabel(child) {
      if (this.bookingChildId === child.id) return 'Close';
      return child.trial_event ? 'Reschedule' : 'Book class';
    },

    toggleTrialBooking(child) {
      this.bookingChildId = this.bookingChildId === child.id ? null : child.id;
    },

    // The POST already returned the updated prospect, so hand it straight up
    // rather than making the list refetch to learn what we were just told.
    onTrialClassBooked(child, payload) {
      this.bookingChildId = null;
      this.$emit('trial-class-booked', { child, prospect: payload && payload.prospect });
    },

    startEditTrialDate(child) {
      this.editingTrialDateId = child.id;
      this.editTrialDateText = this.formatDatetimeLocal(child.trial_scheduled_at);
    },
    cancelEditTrialDate() {
      this.editingTrialDateId = null;
      this.editTrialDateText = '';
    },
    saveTrialDate(child) {
      const iso = this.editTrialDateText ? new Date(this.editTrialDateText).toISOString() : null;
      this.$emit('trial-date-save', { child, iso });
      this.cancelEditTrialDate();
    },
    clearTrialDate(child) {
      this.editTrialDateText = '';
      this.$emit('trial-date-save', { child, iso: null });
      this.cancelEditTrialDate();
    },
    formatDatetimeLocal(isoString) {
      if (!isoString) return '';
      const date = new Date(isoString);
      if (Number.isNaN(date.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      const y = date.getFullYear();
      const m = pad(date.getMonth() + 1);
      const d = pad(date.getDate());
      const h = pad(date.getHours());
      const min = pad(date.getMinutes());
      return `${y}-${m}-${d}T${h}:${min}`;
    },
    formatTrialDate(isoString) {
      if (!isoString) return '';
      const date = new Date(isoString);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    },
    timeAgo(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays === 1) return '1 day ago';
      if (diffDays < 30) return `${diffDays} days ago`;
      if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return months + (months === 1 ? ' month ago' : ' months ago');
      }
      const years = Math.floor(diffDays / 365);
      return years + (years === 1 ? ' year ago' : ' years ago');
    },
  },
};
</script>
